import dayjs from "dayjs";

export const RANGE_PRESETS = [
  { key: "7d", label: "Last 7 days", days: 7 },
  { key: "30d", label: "Last 30 days", days: 30 },
  { key: "90d", label: "Last 90 days", days: 90 },
  { key: "this_month", label: "This month" },
  { key: "last_month", label: "Last month" },
  { key: "ytd", label: "Year to date" },
  { key: "custom", label: "Custom" },
];

/**
 * Options for the dashboard "Last 7 days" stats heading selector.
 * Kept separate from RANGE_PRESETS so it doesn't affect the team chart/table pickers.
 */
export const STATS_RANGE_OPTIONS = [
  { key: "7d", label: "Last 7 days" },
  { key: "30d", label: "Last 30 days" },
  { key: "this_month", label: "Month to date" },
  { key: "last_month", label: "Last month" },
  { key: "qtd", label: "Quarter to date" },
  { key: "last_quarter", label: "Last quarter" },
  { key: "custom", label: "Custom" },
];

const toISO = (d) => dayjs(d).format("YYYY-MM-DD");

const startOfQuarter = (d) =>
  d.date(1).month(Math.floor(d.month() / 3) * 3).startOf("month");

/**
 * Resolve a stored range descriptor to concrete { from, to } ISO date strings.
 * Custom ranges must supply `from` and `to`; presets compute them from `now`.
 */
export function resolveRange(range) {
  if (!range) return null;
  const { preset } = range;
  const now = dayjs();

  switch (preset) {
    case "7d":
      return { from: toISO(now.subtract(7, "day")), to: toISO(now) };
    case "30d":
      return { from: toISO(now.subtract(30, "day")), to: toISO(now) };
    case "90d":
      return { from: toISO(now.subtract(90, "day")), to: toISO(now) };
    case "this_month":
      return { from: toISO(now.startOf("month")), to: toISO(now) };
    case "last_month": {
      const start = now.subtract(1, "month").startOf("month");
      const end = now.subtract(1, "month").endOf("month");
      return { from: toISO(start), to: toISO(end) };
    }
    case "ytd":
      return { from: toISO(now.startOf("year")), to: toISO(now) };
    case "qtd":
      return { from: toISO(startOfQuarter(now)), to: toISO(now) };
    case "last_quarter": {
      const thisQuarterStart = startOfQuarter(now);
      const start = thisQuarterStart.subtract(3, "month");
      const end = thisQuarterStart.subtract(1, "day");
      return { from: toISO(start), to: toISO(end) };
    }
    case "custom": {
      if (!range.from || !range.to) return null;
      const from = toISO(range.from);
      const to = toISO(range.to);
      if (from > to) return null;
      return { from, to };
    }
    default:
      return null;
  }
}

export function getPresetLabel(key) {
  return (
    RANGE_PRESETS.find((p) => p.key === key)?.label ??
    STATS_RANGE_OPTIONS.find((p) => p.key === key)?.label ??
    key
  );
}
